import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { useTranslation } from './hooks/useTranslation';
import { setupAxiosInterceptors } from './utils/auth';
import { toFormUrlEncoded } from './utils/params';
import LoginPage from './components/LoginPage';
import Layout from './components/Layout';

// 導入組件
import EventDetails from './pages/EventsDetails';
import SgPoolsData from './pages/SgPoolsData';
import SimilarEvents from './pages/SimilarEvents';
import NowGoalData from './pages/NowGoalData';
import MethodData from './pages/MethodData';
import UserSettings from './pages/UserSettings';
import AdminUsers from './pages/Users';
import Tasks from './pages/Tasks';
import ErrorLog from './pages/ErrorLog';
import AdminClaude from './pages/Claude';
import NowGoalOUDetails from './pages/NowGoalOUDetails';
import ContainerStatus from './pages/ContainerStatus';
import Docs from './pages/Docs';

const TASK_PERMS = ['/tasks/long-time', '/tasks/short-time', '/tasks/now-goal'];
const ERROR_PERMS = ['/errors/sg-pools', '/errors/now-goal'];


function App() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState(null);  // 只存在記憶體
  const [userRoles, setUserRoles] = useState([]);
  const [visiblePages, setVisiblePages] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(true);  // 開機時嘗試 refresh
  const [dateRange, setDateRange] = useState({
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const { t } = useTranslation();

  const handleLogout = useCallback(async () => {
    try {
      await axios.post(`${backendUrl}/logout`, {}, { withCredentials: true });
    } catch (e) {
      // 不阻塞登出
    }
    setToken(null);
    setUserRoles([]);
    setVisiblePages([]);
    setError(null);
  }, [backendUrl]);

  // 安裝 axios 攔截器（auto-refresh on 401）
  useEffect(() => {
    setupAxiosInterceptors(() => {
      handleLogout();
    });
  }, [handleLogout]);

  // 開機：嘗試用 refresh cookie 取得新的 access token
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(
          `${backendUrl}/token/refresh`,
          null,
          { withCredentials: true }
        );
        if (cancelled) return;
        const { access_token } = res.data;
        if (access_token) {
          setToken(access_token);
          try {
            const decoded = jwtDecode(access_token);
            setUserRoles(decoded.roles || []);
            setVisiblePages(decoded.visible_pages || []);
          } catch {}
        }
      } catch {
        // refresh 失敗 = 還沒登入，正常流程
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    return () => { cancelled = true; };
  }, [backendUrl]);

  // token 更新時解析 roles/visible_pages
  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (decoded.exp * 1000 < Date.now()) {
          setError(t('login.expired'));
          handleLogout();
        } else {
          setUserRoles(decoded.roles || []);
          setVisiblePages(decoded.visible_pages || []);
        }
      } catch (err) {
        setError(t('login.invalidToken'));
        handleLogout();
      }
    }
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const formData = toFormUrlEncoded({ username, password });
      const response = await axios.post(`${backendUrl}/token`, formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        withCredentials: true,
      });
      const { access_token, roles, visible_pages } = response.data;
      setToken(access_token);
      if (roles) setUserRoles(roles);
      if (visible_pages) setVisiblePages(visible_pages);
      setLoading(false);
    } catch (error) {
      setError(error.response?.data?.detail || error.message);
      setLoading(false);
    }
  };

  const handleApplyDates = useCallback(async () => {
    if (!dateRange.start_date || !dateRange.end_date) {
        setError(t('common.invalidDateRange'));
        return;
    }
    if (new Date(dateRange.start_date) > new Date(dateRange.end_date)) {
        setError(t('common.startDateAfterEnd'));
        return;
    }
    setError(null);
  }, [dateRange, t]);


  // 開機檢查中
  if (bootstrapping) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // 未登入: 顯示登入頁面
  if (!token) {
    // 將 App 元件的 state 和函式透過 props 傳遞給 LoginPage
    return (
      <Router>
        <LoginPage
          username={username}
          password={password}
          loading={loading}
          error={error}
          handleLogin={handleLogin}
          setUsername={setUsername}
          setPassword={setPassword}
        />
      </Router>
    );
  }

  // 頁面存取權限判斷
  const isRootOrAdmin = userRoles.includes('root') || userRoles.includes('admin');
  const canAccess = (path) => {
    if (isRootOrAdmin) return true;
    if (!userRoles.includes('user')) return false; // readonly 只能看 settings
    if (path === '/tasks') return TASK_PERMS.some((p) => visiblePages.includes(p));
    if (path === '/errors') return ERROR_PERMS.some((p) => visiblePages.includes(p));
    return visiblePages.includes(path);
  };

  const layoutProps = { userRoles, visiblePages, handleLogout };
  const dateProps = { token, backendUrl, dateRange, setDateRange, error, setError, handleApplyDates };

  // 已登入: 顯示佈局和路由
  return (
    <Router>
        <Routes>
            {/* 預設路徑導向儀表板 */}
            <Route path="/" element={<Navigate to="/settings" replace />} />

            <Route path="/settings" element={
                <Layout {...layoutProps}>
                    <UserSettings token={token} backendUrl={backendUrl} />
                </Layout>
            } />

            <Route path="/docs" element={
                isRootOrAdmin ? (
                    <Layout {...layoutProps}><Docs /></Layout>
                ) : <Navigate to="/settings" replace />
            } />

            <Route path="/sg-pools" element={
                canAccess('/sg-pools') ? (
                    <Layout {...layoutProps}><SgPoolsData {...dateProps} /></Layout>
                ) : <Navigate to="/settings" replace />
            } />
            <Route path="/now-goal" element={
                canAccess('/now-goal') ? (
                    <Layout {...layoutProps}><NowGoalData {...dateProps} /></Layout>
                ) : <Navigate to="/settings" replace />
            } />
            <Route path="/now-goal-ou" element={
                canAccess('/now-goal-ou') ? (
                    <Layout {...layoutProps}><NowGoalOUDetails {...dateProps} /></Layout>
                ) : <Navigate to="/settings" replace />
            } />
            <Route path="/method" element={
                canAccess('/method') ? (
                    <Layout {...layoutProps}><MethodData {...dateProps} /></Layout>
                ) : <Navigate to="/settings" replace />
            } />

            <Route path="/users/admin" element={
                canAccess('/users/admin') ? (
                    <Layout {...layoutProps}><AdminUsers token={token} backendUrl={backendUrl} userRoles={userRoles} /></Layout>
                ) : <Navigate to="/settings" replace />
            } />
            <Route path="/tasks" element={
                canAccess('/tasks') ? (
                    <Layout {...layoutProps}><Tasks token={token} backendUrl={backendUrl} userRoles={userRoles} visiblePages={visiblePages} /></Layout>
                ) : <Navigate to="/settings" replace />
            } />
            <Route path="/errors" element={
                canAccess('/errors') ? (
                    <Layout {...layoutProps}><ErrorLog {...dateProps} userRoles={userRoles} visiblePages={visiblePages} /></Layout>
                ) : <Navigate to="/settings" replace />
            } />
            <Route path="/tasks/long-time" element={<Navigate to="/tasks?source=long_time" replace />} />
            <Route path="/tasks/short-time" element={<Navigate to="/tasks?source=short_time" replace />} />
            <Route path="/tasks/now-goal" element={<Navigate to="/tasks?source=now_goal" replace />} />
            <Route path="/errors/sg-pools" element={<Navigate to="/errors?source=sg_pools" replace />} />
            <Route path="/errors/now-goal" element={<Navigate to="/errors?source=now_goal" replace />} />
            <Route path="/admin/claude" element={
                canAccess('/admin/claude') ? (
                    <Layout {...layoutProps}><AdminClaude token={token} backendUrl={backendUrl} /></Layout>
                ) : <Navigate to="/settings" replace />
            } />
            <Route path="/admin/container-status" element={
                canAccess('/admin/container-status') ? (
                    <Layout {...layoutProps}><ContainerStatus token={token} backendUrl={backendUrl} /></Layout>
                ) : <Navigate to="/settings" replace />
            } />

            {/* 這個路由沒有 Layout，會全螢幕顯示 */}
            <Route path="/events/:dateEventId" element={<EventDetails token={token} backendUrl={backendUrl} />} />
            <Route path="/sg-pools/similar/:dateEventId" element={<SimilarEvents token={token} backendUrl={backendUrl} />} />

            <Route path="*" element={<Navigate to="/settings" replace />} />
        </Routes>
    </Router>
  );
}

export default App;
